import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useProducts, type Product } from '@/hooks/useProducts';

interface Category { id: string; name: string; icon: string; color: string; sort_order: number; }

export default function ProductsTab() {
  const { products, loading: productsLoading, createProduct, updateProduct, deleteProduct: deleteProductDb, toggleProductStatus: toggleProductDb } = useProducts();
  const [categories, setCategories] = useState<Category[]>([]);
  const [productModal, setProductModal] = useState<'add' | 'edit' | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', category: 'Payments', price_1_2_year: 0, price_3_5_year: 0, price_lifetime: 0, requests_per_month: 100000, uptime: '99.9%', endpoints: 10, icon: 'ri-code-box-line', color: '#ff7f0a', status: 'active' as 'active' | 'inactive' });
  const [productSaving, setProductSaving] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  const [catModal, setCatModal] = useState<'add' | 'edit' | null>(null);
  const [editingCat, setEditingCat] = useState<Category | null>(null);
  const [catForm, setCatForm] = useState({ name: '', icon: 'ri-code-box-line', color: '#ff7f0a' });
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from('product_categories').select('*').order('sort_order');
    setCategories(data ?? []);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAddProduct = () => {
    setProductForm({ name: '', description: '', category: categories[0]?.name ?? 'Payments', price_1_2_year: 0, price_3_5_year: 0, price_lifetime: 0, requests_per_month: 100000, uptime: '99.9%', endpoints: 10, icon: 'ri-code-box-line', color: '#ff7f0a', status: 'active' });
    setEditingProduct(null); setProductError(null); setProductModal('add');
  };

  const openEditProduct = (p: Product) => {
    setProductForm({ name: p.name, description: p.description, category: p.category, price_1_2_year: p.price_1_2_year, price_3_5_year: p.price_3_5_year, price_lifetime: p.price_lifetime, requests_per_month: p.requests_per_month, uptime: p.uptime, endpoints: p.endpoints, icon: p.icon, color: p.color, status: p.status });
    setEditingProduct(p); setProductError(null); setProductModal('edit');
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) return;
    setProductSaving(true); setProductError(null);
    if (productModal === 'add') {
      const result = await createProduct({ ...productForm });
      if (!result.success) setProductError(result.error || 'Failed to create product');
      else { setProductModal(null); showToast('Product created!'); }
    } else if (editingProduct) {
      const result = await updateProduct(editingProduct.id, { ...productForm });
      if (!result.success) setProductError(result.error || 'Failed to update product');
      else { setProductModal(null); showToast('Product updated!'); }
    }
    setProductSaving(false);
  };

  const openAddCategory = () => {
    setEditingCat(null);
    setCatForm({ name: '', icon: 'ri-code-box-line', color: '#ff7f0a' });
    setCatError(null);
    setCatModal('add');
  };

  const openEditCategory = (c: Category) => {
    setEditingCat(c);
    setCatForm({ name: c.name, icon: c.icon, color: c.color });
    setCatError(null);
    setCatModal('edit');
  };

  const saveCategory = async () => {
    if (!catForm.name.trim()) return;
    setCatSaving(true); setCatError(null);
    if (catModal === 'edit' && editingCat) {
      const { error } = await supabase.from('product_categories').update({ name: catForm.name.trim(), icon: catForm.icon, color: catForm.color }).eq('id', editingCat.id);
      if (error) { setCatError(error.message); }
      else { await fetchCategories(); setCatModal(null); showToast('Category updated!'); }
    } else {
      const { error } = await supabase.from('product_categories').insert([{ name: catForm.name.trim(), icon: catForm.icon, color: catForm.color, sort_order: categories.length + 1 }]);
      if (error) { setCatError(error.message); }
      else { await fetchCategories(); setCatModal(null); setCatForm({ name: '', icon: 'ri-code-box-line', color: '#ff7f0a' }); showToast('Category added!'); }
    }
    setCatSaving(false);
  };

  const deleteCategory = async (id: string) => {
    await supabase.from('product_categories').delete().eq('id', id);
    await fetchCategories();
    showToast('Category removed');
  };

  return (
    <div className="space-y-4">
      {toast && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm bg-green-50 text-green-700 border border-green-100">
          <i className="ri-checkbox-circle-line" />{toast}
        </motion.div>
      )}

      {/* Categories section */}
      <div className="bg-white rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">Product Categories</h3>
            <p className="text-xs text-gray-400">Manage available categories for products</p>
          </div>
          <button onClick={openAddCategory} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-sidebar text-white rounded-lg cursor-pointer whitespace-nowrap hover:bg-sidebar/90 transition-colors">
            <i className="ri-add-line" />Add Category
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => (
            <div key={c.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-surface-3 bg-surface-2 group">
              <i className={`${c.icon} text-sm`} style={{ color: c.color }} />
              <span className="text-xs font-medium text-gray-700">{c.name}</span>
              <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 ml-1 transition-all">
                <button onClick={() => openEditCategory(c)} className="text-gray-400 hover:text-brand-500 cursor-pointer p-0.5" title="Edit category">
                  <i className="ri-edit-line text-xs" />
                </button>
                <button onClick={() => deleteCategory(c.id)} className="text-gray-300 hover:text-red-400 cursor-pointer p-0.5" title="Delete category">
                  <i className="ri-close-line text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Products table */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">{productsLoading ? 'Loading…' : `${products.length} products total`}</div>
        <button onClick={openAddProduct} className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-add-line" />Add Product
        </button>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-2 border-b border-surface-3">
              {['Product', 'Category', '1-2yr', '3-5yr', 'Lifetime', 'Endpoints', 'Status', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => (
              <motion.tr key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }} className="border-b border-surface-3 last:border-0 hover:bg-surface-2 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${p.color}20` }}>
                      <i className={`${p.icon} text-sm`} style={{ color: p.color }} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-900">{p.name}</div>
                      <div className="text-[10px] text-gray-400 truncate max-w-[140px]">{p.description.slice(0, 50)}{p.description.length > 50 ? '…' : ''}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><span className="text-[11px] bg-surface-2 text-gray-500 px-2 py-0.5 rounded-full">{p.category}</span></td>
                <td className="px-4 py-3 text-xs font-semibold text-gray-700">${p.price_1_2_year}</td>
                <td className="px-4 py-3 text-xs font-semibold text-gray-700">${p.price_3_5_year}</td>
                <td className="px-4 py-3 text-xs font-bold text-brand-600">${p.price_lifetime}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{p.endpoints}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleProductDb(p.id, p.status)} className={`text-[11px] px-2 py-0.5 rounded-full font-medium cursor-pointer transition-colors ${p.status === 'active' ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{p.status}</button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditProduct(p)} className="text-xs px-2 py-1 border border-surface-3 rounded-lg text-gray-500 hover:bg-surface-2 cursor-pointer whitespace-nowrap"><i className="ri-edit-line" /></button>
                    <button onClick={() => deleteProductDb(p.id)} className="text-xs px-2 py-1 border border-red-100 text-red-500 rounded-lg hover:bg-red-50 cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line" /></button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Product Modal */}
      {productModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900">{productModal === 'add' ? 'Add New Product' : 'Edit Product'}</h3>
              <button onClick={() => setProductModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 mb-1 block">Product Name</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.name} onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. PayStream API" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Category</label>
                  <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer" value={productForm.category} onChange={e => setProductForm(f => ({ ...f, category: e.target.value }))}>
                    {categories.map(c => <option key={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Uptime SLA</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.uptime} onChange={e => setProductForm(f => ({ ...f, uptime: e.target.value }))} placeholder="99.9%" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none" rows={3} value={productForm.description} onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the API…" maxLength={500} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Price 1-2yr ($)</label><input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.price_1_2_year} onChange={e => setProductForm(f => ({ ...f, price_1_2_year: Number(e.target.value) }))} /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Price 3-5yr ($)</label><input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.price_3_5_year} onChange={e => setProductForm(f => ({ ...f, price_3_5_year: Number(e.target.value) }))} /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Lifetime ($)</label><input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.price_lifetime} onChange={e => setProductForm(f => ({ ...f, price_lifetime: Number(e.target.value) }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500 mb-1 block">Req/Month</label><input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.requests_per_month} onChange={e => setProductForm(f => ({ ...f, requests_per_month: Number(e.target.value) }))} /></div>
                <div><label className="text-xs text-gray-500 mb-1 block">Endpoints</label><input type="number" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={productForm.endpoints} onChange={e => setProductForm(f => ({ ...f, endpoints: Number(e.target.value) }))} /></div>
              </div>
            </div>
            {productError && <p className="text-xs text-red-500 mt-2">{productError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setProductModal(null)} className="flex-1 py-2.5 border border-surface-3 rounded-xl text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2">Cancel</button>
              <button onClick={saveProduct} disabled={!productForm.name || productSaving} className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                {productSaving ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : productModal === 'add' ? 'Add Product' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Category Modal */}
      {catModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900">{catModal === 'edit' ? 'Edit Category' : 'Add Category'}</h3>
              <button onClick={() => setCatModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-2 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Category Name</label><input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Analytics" /></div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Icon (Remix Icon class)</label>
                <div className="flex items-center gap-2">
                  <input className="flex-1 text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400" value={catForm.icon} onChange={e => setCatForm(f => ({ ...f, icon: e.target.value }))} placeholder="ri-bar-chart-line" />
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg border border-surface-3 bg-surface-2 shrink-0">
                    <i className={`${catForm.icon} text-lg`} style={{ color: catForm.color }} />
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Browse icons at <a href="https://remixicon.com" target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">remixicon.com</a></p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" className="w-10 h-10 border border-surface-3 rounded-lg px-1 py-1 cursor-pointer" value={catForm.color} onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))} />
                  <input className="flex-1 text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 font-mono" value={catForm.color} onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))} placeholder="#ff7f0a" />
                </div>
              </div>
            </div>
            {catError && <p className="text-xs text-red-500 mt-2">{catError}</p>}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setCatModal(null)} className="flex-1 py-2.5 border border-surface-3 rounded-xl text-sm text-gray-600 cursor-pointer whitespace-nowrap hover:bg-surface-2">Cancel</button>
              <button onClick={saveCategory} disabled={!catForm.name || catSaving} className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                {catSaving ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : catModal === 'edit' ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
